import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types';

interface SetupStatus {
  needsSetup?: boolean;
  hasCompanyName?: boolean;
}

const FirstRunGuard: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const status = await apiService.get<SetupStatus>('/system/public/setup-status');
        if (cancelled) {
          return;
        }

        // Only a stored company_name completes setup. A logo-only or empty
        // settings row, or a missing/failed status payload, must not.
        if (typeof status?.hasCompanyName === 'boolean') {
          setNeedsSetup(!status.hasCompanyName);
          return;
        }

        setNeedsSetup(status?.needsSetup === true);
      } catch (error) {
        console.error('Error checking first-run setup:', error);
        if (!cancelled) {
          setNeedsSetup(true);
        }
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (needsSetup === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (needsSetup && user?.role === UserRole.SUPERADMIN) {
    return <Navigate to="/setup" replace />;
  }

  return <Outlet />;
};

export default FirstRunGuard;
